// steps/Step2.js
import React, { useState, useRef, useEffect } from "react";
import {
  FaTrash,
  FaPlus,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import "../tutorRegistration/TutorRegistration.css";
import { useAuth } from "../../context/AuthContext";
import {
  PROGRAM_CATEGORIES,
  SELECTABLE_PROGRAM_CATEGORIES,
} from "../../data/tutorPrograms";
import InstitutionSelect from "../institutionSelect/InstitutionSelect";
import {
  FIELD_OF_STUDY_GROUPS,
  DEGREE_OPTIONS,
} from "../../data/nigerianInstitutions";

/* ─────────────────────────────────────────────
   Teaching-mode options (expanded)
───────────────────────────────────────────── */
// Harmonized to exactly 3 modes everywhere (frontend, admin, backend) —
// see scripts/migrateTeachingModes.js for the one-time data migration from
// the old 6-value scheme these replace.
const TEACHING_MODES = [
  {
    value: "online",
    label: "Online",
    icon: "🎥",
    desc: "Real-time virtual sessions with students",
  },
  {
    value: "hybrid",
    label: "Hybrid",
    icon: "🔀",
    desc: "Combination of online and physical classes",
  },
  {
    value: "physical",
    label: "Physical",
    icon: "🏫",
    desc: "Group or individual sessions in a physical centre",
  },
];

const RATE_PERIODS = [
  { value: "hour", label: "Per Hour (₦)" },
  { value: "day", label: "Per Day (₦)" },
  { value: "week", label: "Per Week (₦)" },
  { value: "month", label: "Per Month (₦)" },
  { value: "session", label: "Per Session (₦)" },
  { value: "term", label: "Per Term (₦)" },
];

/* ─────────────────────────────────────────────
   Helper: CategoryBlock
   Shows courses + custom input + pricing for
   one selected category
───────────────────────────────────────────── */
const CategoryBlock = ({
  group,
  categoryData,
  onChange,
  onRemove,
  errors = {},
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedCourses = categoryData.courses || [];
  const customCourse = categoryData.customCourse || "";
  const rate = categoryData.rate || "";
  const period = categoryData.period || "";

  const toggleCourse = (course) => {
    const exists = selectedCourses.includes(course);
    if (!exists && selectedCourses.length >= 5) return;
    const updated = exists
      ? selectedCourses.filter((c) => c !== course)
      : [...selectedCourses, course];
    onChange({ ...categoryData, courses: updated });
  };

  const addCustomCourse = () => {
    const trimmed = customCourse.trim();
    if (!trimmed) return;
    if (selectedCourses.includes(trimmed)) return;
    if (selectedCourses.length >= 5) return;
    onChange({
      ...categoryData,
      courses: [...selectedCourses, trimmed],
      customCourse: "",
    });
  };

  const removeCourse = (course) => {
    onChange({
      ...categoryData,
      courses: selectedCourses.filter((c) => c !== course),
    });
  };

  const groupItems =
    PROGRAM_CATEGORIES.find((g) => g.group === group)?.items || [];

  return (
    <div className="category-block">
      {/* Header */}
      <div className="category-block-header">
        <span className="category-block-title">{group}</span>
        <button
          type="button"
          className="category-remove-btn"
          onClick={onRemove}
          title={`Remove ${group}`}
        >
          <FaTimes />
        </button>
      </div>

      {/* Courses dropdown */}
      <div className="form-group" style={{ marginBottom: "12px" }}>
        <label className="form-label required">
          Courses <span className="limit-hint">(max 5)</span>
        </label>

        <div className="custom-dropdown-container" ref={dropdownRef}>
          <div
            className={`dropdown-trigger tutor-input-field ${dropdownOpen ? "active" : ""}`}
            onClick={() => setDropdownOpen((p) => !p)}
          >
            <span>
              {selectedCourses.length
                ? `${selectedCourses.length} course${selectedCourses.length > 1 ? "s" : ""} selected`
                : "Select courses…"}
            </span>
            <span className="dropdown-arrow">
              {dropdownOpen ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>

          {dropdownOpen && (
            <div className="custom-dropdown-panel">
              {groupItems.map((item) => {
                const checked = selectedCourses.includes(item);
                const disabled = !checked && selectedCourses.length >= 5;
                return (
                  <label
                    key={item}
                    className={`dropdown-item ${disabled ? "disabled" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="category-checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggleCourse(item)}
                    />
                    <span>{item}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {selectedCourses.length > 0 && (
          <div
            className="selected-tags-container compact"
            style={{ marginTop: 8 }}
          >
            {selectedCourses.map((c) => (
              <span key={c} className="selected-tag compact-tag">
                {c}
                <button
                  type="button"
                  className="tag-remove-btn"
                  onClick={() => removeCourse(c)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {selectedCourses.length >= 5 && (
          <p className="course-limit-hint">Maximum of 5 courses reached</p>
        )}
      </div>

      {/* Custom "Others" course input */}
      <div className="form-group" style={{ marginBottom: "12px" }}>
        <label className="form-label">
          Add a Custom Course{" "}
          <span className="optional-label">(not in the list above)</span>
        </label>
        <div className="custom-course-input-row">
          <input
            type="text"
            className="tutor-input-field"
            placeholder="e.g. Advanced Robotics, Igbo Language…"
            value={customCourse}
            disabled={selectedCourses.length >= 5}
            onChange={(e) =>
              onChange({ ...categoryData, customCourse: e.target.value })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addCustomCourse();
              }
            }}
          />
          <button
            type="button"
            className="add-custom-course-btn"
            onClick={addCustomCourse}
            disabled={!customCourse.trim() || selectedCourses.length >= 5}
            title="Add custom course"
          >
            <FaPlus />
          </button>
        </div>
        <p className="note">
          Press Enter or click + to add. Counts toward the 5-course limit.
        </p>
      </div>

      {/* Pricing for this category */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label required">Pricing Rate for {group}</label>
        <div className="rate-input-group">
          <select
            value={period}
            onChange={(e) =>
              onChange({ ...categoryData, period: e.target.value })
            }
            className="rate-period-select"
          >
            <option value="" disabled>
              Select period…
            </option>
            {RATE_PERIODS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
          <input
            type="number"
            className="tutor-input-field rate-amount-input"
            placeholder={`Rate per ${period}`}
            value={rate}
            min="0"
            onChange={(e) =>
              onChange({ ...categoryData, rate: e.target.value })
            }
            style={{ flex: 1 }}
          />
        </div>
        {errors[`rate_${group}`] && (
          <p className="error">{errors[`rate_${group}`]}</p>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Main Step2 Component
───────────────────────────────────────────── */
const Step2 = ({
  formData,
  setFormData,
  errors,
  handleInputChange,
  handleOtherInputChange,
  handleAddSchool,
  handleDeleteSchool,
  registrationType,
  handleFileChange,
}) => {
  const { user } = useAuth();

  useEffect(() => {
    // Scroll to top smoothly when the component mounts / route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth" if you prefer a gentle scroll
    });
  }, []);

  /* ── formData shape additions ─────────────────
     selectedCategories: string[]                 — selected program group names
     categoryDetails:    { [group]: { courses, customCourse, rate, period } }
     teachingModes:      string[]                 — subset of ["online","hybrid","physical"]
  ─────────────────────────────────────────────── */

  // Ensure arrays/objects exist (defensive)
  const selectedCategories = formData.selectedCategories || [];
  const categoryDetails = formData.categoryDetails || {};
  const teachingModes = formData.teachingModes || [];

  /* ── Category helpers ──────────────────────── */
  const toggleCategory = (groupName) => {
    const exists = selectedCategories.includes(groupName);
    let updatedCats;
    let updatedDetails = { ...categoryDetails };

    if (exists) {
      updatedCats = selectedCategories.filter((g) => g !== groupName);
      delete updatedDetails[groupName];
    } else {
      if (selectedCategories.length >= 2) return; // ← cap at 2
      updatedCats = [...selectedCategories, groupName];
      updatedDetails[groupName] = {
        courses: [],
        customCourse: "",
        rate: "",
        period: "",
      };
    }

    setFormData((prev) => ({
      ...prev,
      selectedCategories: updatedCats,
      categoryDetails: updatedDetails,
    }));
  };

  const updateCategoryDetail = (groupName, data) => {
    setFormData((prev) => ({
      ...prev,
      categoryDetails: { ...prev.categoryDetails, [groupName]: data },
    }));
  };

  /* ── Teaching mode helpers ─────────────────── */
  // const toggleTeachingMode = (value) => {
  //   const exists = teachingModes.includes(value);
  //   const updated = exists
  //     ? teachingModes.filter((m) => m !== value)
  //     : [...teachingModes, value];
  //   setFormData((prev) => ({ ...prev, teachingModes: updated }));
  // };

  const toggleTeachingMode = (value) => {
    const alreadySelected = teachingModes.includes(value);
    // clicking the active mode deselects it; clicking another replaces it
    setFormData((prev) => ({
      ...prev,
      teachingModes: alreadySelected ? [] : [value],
    }));
  };

  /* ── Shared field renderers ────────────────── */
  const handleOtherChange = (field) => (e) =>
    handleOtherInputChange(field, e.target.value);

  const renderDropdownField = (name, label, options) => (
    <div className="form-group">
      <label>{label} *</label>
      <select
        name={name}
        value={formData[name] || ""}
        onChange={handleInputChange}
        className="tutor-input-field"
      >
        <option value="">Select {label}</option>
        {Array.isArray(options[0]?.fields)
          ? options.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.fields.map((field) => (
                  <option
                    key={field}
                    value={field.toLowerCase().replace(/\s+/g, "-")}
                  >
                    {field}
                  </option>
                ))}
              </optgroup>
            ))
          : options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
      </select>

      {formData[name] === "other" && (
        <div style={{ marginTop: 8 }}>
          <input
            type="text"
            name={`${name}Other`}
            placeholder={`Specify your ${label.toLowerCase()}`}
            value={formData[`${name}Other`] || ""}
            onChange={handleOtherChange(name)}
            className="tutor-input-field other-input"
          />
        </div>
      )}

      {errors[name] && <p className="error">{errors[name]}</p>}
      {errors[`${name}Other`] && (
        <p className="error">{errors[`${name}Other`]}</p>
      )}
    </div>
  );

  // const FIELD_OF_STUDY_OPTIONS = FIELD_OF_STUDY_GROUPS.flatMap((group) =>
  //   group.fields.map((field) => ({
  //     value: field.toLowerCase().replace(/\s+/g, "-"),
  //     label: field,
  //   })),
  // );

  const FIELD_OF_STUDY_OPTIONS = FIELD_OF_STUDY_GROUPS.flatMap((group) =>
    group.fields.map((field) => ({
      value: field.toLowerCase().replace(/\s+/g, "-"),
      label: field,
    })),
  ).sort((a, b) => a.label.localeCompare(b.label));

  /* ── Tutor education section ───────────────── */
  const renderTutorFields = () => (
    <>
      <div className="form-group">
        <label>Name of School *</label>
        <InstitutionSelect
          value={formData.schoolName || ""}
          onChange={(val) =>
            setFormData((prev) => ({ ...prev, schoolName: val }))
          }
          error={errors.schoolName}
          placeholder="Search university, polytechnic, college…"
        />
      </div>
      {renderDropdownField("degree", "Degree", DEGREE_OPTIONS)}
      {renderDropdownField(
        "fieldOfStudy",
        "Field of Study",
        FIELD_OF_STUDY_OPTIONS,
      )}
    </>
  );

  /* ── Academy credentials section ──────────── */
  const renderFileField = (name, label, accept = "image/*,application/pdf") => (
    <div className="form-group">
      <label>{label}</label>
      <input
        type="file"
        name="id"
        accept={accept}
        onChange={(e) => handleFileChange(e, name)}
        className="tutor-input-field"
      />
      {errors[name] && <p className="error">{errors[name]}</p>}
      <p className="note">
        Upload accreditation/CAC document (PDF or image, max 5MB)
      </p>
    </div>
  );

  const renderAcademyFields = () =>
    renderFileField(
      "accreditationDoc",
      "Registration / CAC / Accreditation Document Upload",
    );

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <>
      {/* ── Section 1: Credentials ──────────────── */}
      <div className="form-title">
        <h2>
          {registrationType === "academy"
            ? "Add your Centre Credentials (Optional)"
            : "Add Your Education Background"}
        </h2>
        <p className="subtitle">
          {registrationType === "academy"
            ? "Provide your centre document and credentials"
            : "Upgrade your profile by providing your highest education information"}
        </p>
      </div>

      {registrationType === "academy"
        ? renderAcademyFields()
        : renderTutorFields()}

      {/* ── Cover Image Upload ─────────────────────── */}
      <div className="section-divider" />
      <div className="form-title">
        <h2>
          Profile Cover Image{" "}
          <span
            style={{ fontWeight: 400, fontSize: "0.9em", color: "#64748b" }}
          >
            (Optional)
          </span>
        </h2>
        <p className="subtitle">
          Add a banner image to personalise your profile page.
        </p>
      </div>

      <div className="form-group">
        <label>Cover Image</label>

        {/* Preview — shown once a file is picked */}
        {formData.coverImage && (
          <div
            style={{
              marginBottom: 12,
              borderRadius: 10,
              overflow: "hidden",
              height: 140,
              position: "relative",
            }}
          >
            <img
              src={URL.createObjectURL(formData.coverImage)}
              alt="Cover preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            {/* Remove button */}
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, coverImage: null }))
              }
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(0,0,0,0.5)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: 28,
                height: 28,
                cursor: "pointer",
                fontSize: 16,
                lineHeight: "28px",
                textAlign: "center",
              }}
              aria-label="Remove cover image"
            >
              ×
            </button>
          </div>
        )}

        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => handleFileChange(e, "coverImage")}
          className="tutor-input-field"
        />
        <p className="note">
          JPG, PNG or WebP · max 5 MB · recommended 1200 × 400 px
        </p>
        {errors.coverImage && <p className="error">{errors.coverImage}</p>}
      </div>

      {/* ── Section 2: Teaching Modes ───────────── */}
      <div className="section-divider" />
      <h2>Mode of Teaching</h2>
      <p className="subtitle">
        {/* Select all the teaching formats you offer. You can choose multiple. */}
        Select the teaching format you offer.
      </p>

      <div className="teaching-modes-grid">
        {TEACHING_MODES.map((mode) => {
          const selected = teachingModes.includes(mode.value);
          return (
            <button
              key={mode.value}
              type="button"
              className={`teaching-mode-card ${selected ? "selected" : ""}`}
              onClick={() => toggleTeachingMode(mode.value)}
            >
              <span className="mode-icon">{mode.icon}</span>
              <span className="mode-label">{mode.label}</span>
              <span className="mode-desc">{mode.desc}</span>
              {selected && <span className="mode-check">✓</span>}
            </button>
          );
        })}
      </div>

      {/* Selected modes display */}
      {teachingModes.length > 0 && (
        <div className="selected-tags-container" style={{ marginTop: 10 }}>
          {teachingModes.map((m) => {
            const found = TEACHING_MODES.find((t) => t.value === m);
            return (
              <span key={m} className="selected-tag">
                {found?.icon} {found?.label}
                <button
                  type="button"
                  className="tag-remove-btn"
                  onClick={() => toggleTeachingMode(m)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {errors.teachingModes && (
        <p className="error" style={{ marginTop: 6 }}>
          {errors.teachingModes}
        </p>
      )}

      {/* ── Free Intro/Trial Session ────────── */}
      <div className="form-group" style={{ marginTop: 20 }}>
        <label className="form-label">
          Do you offer a free Intro/First Session?
        </label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="radio"
              name="offersIntroSession"
              checked={formData.offersIntroSession === true}
              onChange={() =>
                setFormData((prev) => ({ ...prev, offersIntroSession: true }))
              }
            />
            Yes
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="radio"
              name="offersIntroSession"
              checked={!formData.offersIntroSession}
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  offersIntroSession: false,
                }))
              }
            />
            No
          </label>
        </div>
      </div>

      {/* ── Section 3: Course Categories ────────── */}
      <div className="section-divider" />
      <h2>Course Categories &amp; Courses Offered</h2>
      <p className="subtitle">
        Select <strong>up to 3</strong> course categories, then choose specific
        courses and set your pricing for each category.
      </p>

      {/* Category multi-select grid */}
      <div className="form-group">
        <label className="form-label required">Course Categories</label>
        <div className="category-select-grid">
          {PROGRAM_CATEGORIES.filter((g) =>
            SELECTABLE_PROGRAM_CATEGORIES.includes(g.group),
          ).map((group) => {
            const active = selectedCategories.includes(group.group);
            return (
              <button
                key={group.group}
                type="button"
                className={`category-chip ${active ? "selected" : ""} ${
                  !active && selectedCategories.length >= 3 ? "disabled" : ""
                }`}
                onClick={() => toggleCategory(group.group)}
                disabled={!active && selectedCategories.length >= 3}
              >
                {active && <span className="chip-check">✓ </span>}
                {group.group}
              </button>
            );
          })}
        </div>
        {errors.selectedCategories && (
          <p className="error">{errors.selectedCategories}</p>
        )}
      </div>

      {/* Per-category detail blocks */}
      {selectedCategories.length > 0 && (
        <div className="category-blocks-list">
          {selectedCategories.map((groupName) => (
            <CategoryBlock
              key={groupName}
              group={groupName}
              categoryData={categoryDetails[groupName] || {}}
              onChange={(data) => updateCategoryDetail(groupName, data)}
              onRemove={() => toggleCategory(groupName)}
              errors={errors}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default Step2;
